/**
 * Protected Route Implementation
 *
 * This file implements route protection using authentication and authorization.
 * It uses a custom wrapper for ElitAuthProvider to avoid TypeScript build errors.
 */
// IMPORTANT: We're not importing ElitAuthProvider directly due to TypeScript build errors
// import { ElitAuthProvider } from "@elit-hair/elit-tools";
import BRANDS from "@elit-hair/brands";
import { useTranslation } from "react-i18next";

import ElitAuthWrapper from "@/components/ElitAuthWrapper";
import { ENV } from "@/constants/env";
import { authEndpoints } from "@/endpoints";
import useCachedUserData from "@/hooks/useCachedUserData";
import { useUserPermissionsSync } from "@/hooks/useUserPermissionsSync";
import useAuth from "@/providers/hooks/useAuthProvider";
import { checkUserPermissions } from "@/utils/permissionUtils";

import LoadingScreen from "../../components/LoadingScreen";
import UnauthorizedScreen from "../../components/UnauthorizedScreen";

/**
 * ProtectedRoute component that wraps children with authentication and authorization checks
 *
 * @param children - The components to render when authentication and authorization pass
 * @param permissions - Optional array of permission codes required to access the route
 * @returns A component that handles authentication and authorization flows
 */
export const ProtectedRoute = ({ children, permissions }: { children: React.ReactNode; permissions?: string[] }) => {
    const { i18n } = useTranslation();
    const { auth, setAuth } = useAuth();
    const { refreshUserPermissions } = useUserPermissionsSync(60000, false);
    const { effectiveUserData } = useCachedUserData();

    // Check if user is authorized
    const isAuthorized = !!effectiveUserData && checkUserPermissions(effectiveUserData?.permissionCodes, permissions);

    return (
        <ElitAuthWrapper
            provider="elithair"
            exchangeCodeUrl={authEndpoints.exchangeCode}
            customValidationUrl={authEndpoints.ssoLogin}
            authenticated={!!auth && !!effectiveUserData}
            authorized={isAuthorized}
            style="D"
            appName="Elithair Panel"
            lang={i18n.language}
            onAuthenticated={(accessToken, refreshToken) => {
                setAuth({ accessToken, refreshToken });
                // Immediately refresh user permissions after authentication
                refreshUserPermissions();
            }}
            redirectingScreen={<LoadingScreen />}
            authenticatingScreen={<LoadingScreen />}
            unauthorizedScreen={<UnauthorizedScreen />}
            authServices={[
                {
                    img: BRANDS.elithair.logoSquare || "",
                    color: "#eee",
                    url: ENV.ZOHO_ELITHAIR_SSO_URL,
                },
                // {
                //     img: BRANDS.biohair.logoSquare || "",
                //     color: "#eee",
                //     url: ENV.ZOHO_BIOHAIR_SSO_URL,
                // },
                // {
                //     img: BRANDS.rhinomedica.logoSquare || "",
                //     color: "#eee",
                //     url: ENV.ZOHO_BIOHAIR_SSO_URL,
                // },
                // {
                //     img: BRANDS.yessmile.logoSquare || "",
                //     color: "#eee",
                //     url: ENV.ZOHO_BIOHAIR_SSO_URL,
                // },
            ]}
        >
            {children}
        </ElitAuthWrapper>
    );
};

export default ProtectedRoute;
